type WriteAccessResult = "allowed" | "cancelled" | "error";

export interface WriteAccessBridge {
    initData: string;
    initDataUnsafe: { user?: { id: number; allows_write_to_pm?: boolean } };
    isVersionAtLeast(version: string): boolean;
    requestWriteAccess?: (callback: (allowed: boolean) => void) => void;
}

/** One controller per mounted owner; storage is only a prompt-suppression hint. */
export function createWriteAccessRequester() {
    const attempted = new Set<string>();
    return (input: {
        bridge: WriteAccessBridge | null;
        scope: string;
        telegramUserId: string;
        storage: Pick<Storage, "getItem" | "setItem"> | null;
        onRequested: () => void;
        onResult: (result: WriteAccessResult) => void;
    }): void => {
        const { bridge, storage } = input;
        const key = `pbtg:tg-write-request:v1:${input.scope}:${input.telegramUserId}`;
        if (attempted.has(key)) return;
        if (
            !bridge?.initData ||
            String(bridge.initDataUnsafe.user?.id) !== input.telegramUserId ||
            !bridge.requestWriteAccess ||
            !bridge.isVersionAtLeast("6.9")
        )
            return;
        if (bridge.initDataUnsafe.user?.allows_write_to_pm === true) return;
        try {
            if (storage?.getItem(key) === "1") return;
        } catch {
            /* Unavailable storage must not break gameplay. */
        }
        attempted.add(key);
        let completed = false;
        try {
            input.onRequested();
            bridge.requestWriteAccess((allowed) => {
                if (completed) return;
                completed = true;
                try {
                    storage?.setItem(key, "1");
                } catch {
                    /* Best effort. */
                }
                input.onResult(allowed ? "allowed" : "cancelled");
            });
        } catch {
            // Do not retry a bridge error in this mount, or persist it as a refusal.
            if (!completed) input.onResult("error");
        }
    };
}
